import React, { useContext, useMemo, useState } from "react";
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  ColumnDef,
} from "@tanstack/react-table";
import {
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Popover,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Variants,
} from "@sede-enterprise/shell-ds-react-framework";

import { Link, useNavigate } from "react-router-dom";
import { AnchorSpan } from "../styledComponent/StyledComponent";
import { formatDateToDDMMMYYYY } from "../../services/UtilService";
import { Ellipsis } from "@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components";
import TransactionActionModal from "../TransactionActionModal/TransactionActionModal";
import { postData, postDataFile } from "../../services/ApiService";
import { ApiUrls } from "../../helpers/ApiUrls";
import ResponseErrorModal from "../responseErrorModal/ResponseErrorModal";
import AuthorizeContext from "../applayout/AuthrizeContext";
import { useToast } from "rc-toastr";
import { APIErrorResponseInterface } from "../../helpers/InterfacesConstants";

type TransactionHistory = {
  ticket: string;
  receivedDate: string;
  transactionDate: string;
  country: string;
  site: string;
  customer: string;
  customerCode: string;
  errorType: string;
  totalErrors: string;
  type: string;
  headerId: string;
  is_Mi_Bulk_Tkt_Processing: boolean;
};

const TransactionsTable = (props: {
  data: TransactionHistory[];
  refreshPage: () => void;
  onMISelectionChange?: (selectedIds: string[], count: number) => void;
}) => {
  const data = props.data;
  const navigate = useNavigate();
  const userPermissions = useContext(AuthorizeContext);
  const [transactionAction, setTransactionAction] = useState<
    "Cancel" | "Duplicate"
  >("Cancel");

  const [transactionDetails, setTransactionDetails] = useState<{
    transactionId: string;
    ticket: string;
    reason: string;
  }>({
    transactionId: "",
    ticket: "",
    reason: "",
  });

  const [apiErrorResponse, setApiErrorResponse] =
    useState<APIErrorResponseInterface | null>();

  const [openTransactionModal, setOpenTransactionModal] = useState(false);
  const { toast } = useToast();
  const miPermission = userPermissions?.permissions.find(
    (permission) => permission.permissionName === "MI"
  );

  //cr4 mi mass aubmission
  const MAX_MI_SELECTION = 50;
  const [miRowSelection, setMIRowSelection] = useState<Record<string, boolean>>(
    {}
  );

  const warningTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const getSelectedMIRowsCount = () => {
    return Object.keys(miRowSelection).filter((id) => miRowSelection[id])
      .length;
  };

  const getSelectedMiTicketIds = (): string[] => {
    return Object.keys(miRowSelection).filter((id) => miRowSelection[id]);
  };

  React.useEffect(() => {
    if (!data || data.length === 0) return;

    // Remove any selected tickets that are now processing (flag changed from 0 to 1)
    setMIRowSelection((prevSelection) => {
      const newSelection = { ...prevSelection };
      let hasChanges = false;

      Object.keys(newSelection).forEach((headerId) => {
        const ticket = data.find((row: any) => row.headerId === headerId);

        // If ticket is now processing (flag = 1), remove from selection
        if (ticket?.is_Mi_Bulk_Tkt_Processing === true) {
          delete newSelection[headerId];
          hasChanges = true;
        }
      });

      return hasChanges ? newSelection : prevSelection;
    });
  }, [data]);

  // Notify parent when selection changes
  React.useEffect(() => {
    const selectedIds = getSelectedMiTicketIds();
    const count = selectedIds.length;
    if (props.onMISelectionChange) {
      props.onMISelectionChange(selectedIds, count);
    }
  }, [miRowSelection]);

  const columns = useMemo<ColumnDef<TransactionHistory>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => {
          // Get all selectable rows (not processing)
          const selectableRows = table
            .getRowModel()
            .rows.filter(
              (row) => row.original.is_Mi_Bulk_Tkt_Processing === false
            );

          // Count how many selectable rows are currently selected
          const selectedSelectableCount = selectableRows.filter(
            (row) => miRowSelection[row.original.headerId]
          ).length;

          // Maximum we can select
          const maxSelectable = Math.min(
            selectableRows.length,
            MAX_MI_SELECTION
          );

          // Header checkbox states
          const isAllMIRowsSelected =
            selectableRows.length > 0 &&
            selectedSelectableCount >= maxSelectable;

          const isSomeMIRowsSelected =
            selectedSelectableCount > 0 &&
            selectedSelectableCount < maxSelectable;

          const handleMIHeaderCheckboxChange = (
            e: React.ChangeEvent<HTMLInputElement>
          ) => {
            e.stopPropagation();
            const isChecked = e.target.checked;

            if (isChecked) {
              const newMiSelection: Record<string, boolean> = {};
              const rowsToSelect = selectableRows.slice(0, MAX_MI_SELECTION);

              rowsToSelect.forEach((row) => {
                newMiSelection[row.original.headerId] = true;
              });

              setMIRowSelection(newMiSelection);
            } else {
              //Deselecting all rows by setting sate to empty
              setMIRowSelection({});
            }
          };

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox
                checked={isAllMIRowsSelected}
                indeterminate={isSomeMIRowsSelected}
                onChange={handleMIHeaderCheckboxChange}
                disabled={selectableRows.length === 0}
                style={{
                  transform: "scale(0.8)",
                  opacity: selectableRows.length === 0 ? 0.5 : 1,
                }}
              ></Checkbox>
            </div>
          );
        },
        cell: ({ row }) => {
          const isProcessing = row.original.is_Mi_Bulk_Tkt_Processing === true;

          const handleMIRowCheckboxChange = (
            e: React.ChangeEvent<HTMLInputElement>
          ) => {
            e.stopPropagation();
            const isChecked = e.target.checked;
            const selectedMIRowCount = getSelectedMIRowsCount();

            // Prevent selection if already at max limit
            if (isChecked && selectedMIRowCount >= MAX_MI_SELECTION) {
              // Only show warning once per "session" until user deselects
              if (!warningTimeoutRef.current) {
                toast.warning(
                  `Maximum ${MAX_MI_SELECTION} tickets can be selected`
                );

                warningTimeoutRef.current = setTimeout(() => {
                  warningTimeoutRef.current = null;
                }, 2000);
              }
              return;
            }

            // Clear timeout on any successful action
            if (warningTimeoutRef.current) {
              clearTimeout(warningTimeoutRef.current);
              warningTimeoutRef.current = null;
            }

            // Update selection state for this specific row
            setMIRowSelection((prev) => ({
              ...prev,
              [row.original.headerId]: isChecked,
            }));
          };

          return (
            <Checkbox
              checked={!!miRowSelection[row.original.headerId]}
              onChange={handleMIRowCheckboxChange}
              disabled={isProcessing}
              style={{
                transform: "scale(0.8)",
                opacity: isProcessing ? 0.4 : 1,
                cursor: isProcessing ? "not-allowed" : "pointer",
              }}
            ></Checkbox>
          );
        },
        size: 50,
        minSize: 40,
        maxSize: 60,
      },
      {
        accessorFn: (row) => row.ticket,
        header: "Ticket#",
        cell: ({ row }) => {
          const { ticket, headerId } = row.original;
          return (
            <Link to={"/transactions/" + headerId}>
              {" "}
              <AnchorSpan>{ticket}</AnchorSpan>
            </Link>
          );
        },
        id: "ticket",
        size: 60,
      },
      {
        accessorFn: (row) => row.receivedDate,
        cell: (info) => formatDateToDDMMMYYYY(String(info.getValue())),
        header: "Received Date",
        id: "receivedDate",
        size: 80,
      },
      {
        accessorFn: (row) => row.transactionDate,
        cell: (info) => formatDateToDDMMMYYYY(String(info.getValue())),
        header: "Transaction Date",
        id: "transactionDate",
        size: 85,
      },
      {
        accessorFn: (row) => row.country,
        id: "country",
        cell: (info) => info.getValue(),
        header: "Country",
        size: 60,
      },
      {
        accessorKey: "site",
        header: "Site",
        id: "site",
        cell: (info) => info.getValue(),
        size: 60,
      },
      {
        accessorKey: "customer",
        header: "Customer",
        id: "customer",
        cell: (info) => info.getValue(),
        size: 100,
      },
      {
        accessorKey: "customerCode",
        header: "Cus Code",
        id: "customerCode",
        cell: (info) => info.getValue(),
        size: 72,
      },
      {
        accessorKey: "errorType",
        header: "Error Type",
        id: "errorType",
        cell: (info: { getValue: () => any }) => (
          <div
            style={{
              maxWidth: "120px",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        size: 100,
      },
      {
        accessorKey: "tcNumber",
        header: "TC Number",
        id: "tcNumber",
        cell: (info) => info.getValue(),
        size: 100,
      },
      {
        accessorKey: "totalErrors",
        header: "Total Errors",
        id: "totalErrors",
        cell: ({ row }) => {
          const { totalErrors, type } = row.original;
          if (type === "Error") {
            const stl = {
              display: "inline-block",
              maxWidth: "40px",
              height: "23px",
              borderRadius: "15px",
              backgroundColor: "#DD1D21",
              color: "#FFFFFF",
              marginLeft: "6px",
              paddingLeft: "8px",
              paddingRight: "8px",
            };
            return <span style={stl}>{totalErrors}</span>;
          } else if (type === "MI") {
            const stl = {
              display: "inline-block",
              maxWidth: "40px",
              height: "23px",
              borderRadius: "15px",
              backgroundColor: "#B25F00",
              color: "#FFFFFF",
              marginLeft: "6px",
              paddingLeft: "8px",
              paddingRight: "8px",
            };
            return <span style={stl}>{totalErrors} </span>;
          }
        },
        size: 85,
      },
      {
        accessorFn: (row) => row.headerId,
        header: "Actions",
        id: "edit",
        cell: ({ row }) => {
          const { ticket, headerId } = row.original;
          const menuItems: string[] = [];
          if (miPermission?.canRead) {
            menuItems.push("View");
          }
          if (miPermission?.canWrite) {
            menuItems.push("Edit", "Cancel", "Duplicate");
          }
          return (
            <Popover
              popup={
                <Menu style={{ width: "150px" }}>
                  {menuItems.indexOf("View") != -1 && (
                    <Link to={"/transactions/" + headerId}>
                      {" "}
                      <MenuItem
                        key="1"
                        style={{ fontWeight: "650", paddingLeft: "16px" }}
                      >
                        View
                      </MenuItem>
                    </Link>
                  )}
                  {menuItems.indexOf("Edit") != -1 && (
                    <Link to={"/update/" + headerId}>
                      {" "}
                      <MenuItem
                        key="2"
                        style={{ fontWeight: "650", paddingLeft: "16px" }}
                      >
                        Edit
                      </MenuItem>
                    </Link>
                  )}
                  {menuItems.indexOf("Cancel") != -1 && (
                    <MenuItem
                      key="3"
                      onClick={() => menuClick("Cancel", headerId, ticket)}
                      style={{ fontWeight: "650" }}
                    >
                      Cancel
                    </MenuItem>
                  )}
                  {menuItems.indexOf("Duplicate") != -1 && (
                    <MenuItem
                      key="4"
                      onClick={() => menuClick("Duplicate", headerId, ticket)}
                      style={{ fontWeight: "650" }}
                    >
                      Duplicate
                    </MenuItem>
                  )}
                </Menu>
              }
            >
              <Button
                variant={Variants.Transparent}
                icon={<Ellipsis />}
                style={{
                  float: "right",
                  justifyContent: "end",
                  minWidth: "35px",
                }}
                aria-label="Row Menu Ellipsis icon"
              ></Button>
            </Popover>
          );
        },
        size: 60,
      },
    ],
    [miRowSelection, toast]
  );

  const menuClick = (strAction: string, headerId: string, ticket: string) => {
    setTransactionDetails({
      transactionId: headerId,
      ticket: ticket,
      reason: "",
    });
    setTransactionAction(strAction as "Cancel" | "Duplicate");
    setOpenTransactionModal(true);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection: miRowSelection,
    },
    onRowSelectionChange: setMIRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.headerId,
  });

  const getActionData = (action: "Cancel" | "Duplicate") => {
    if (action === "Cancel") {
      return {
        url: ApiUrls.cancelTransaction,
        actionText: "Cancelled",
        modalTitle: "Cancel Transaction",
      };
    }

    return {
      url: ApiUrls.duplicateTransaction,
      actionText: "Marked as Duplicate",
      modalTitle: "Mark as Duplicate Transaction",
    };
  };

  const handleTransactionAction = async (data: any) => {
    const actionData = getActionData(transactionAction);
    const url = actionData.url + transactionDetails.transactionId;
    await postDataFile(url, data)
      .then((response) => {
        setOpenTransactionModal(false);
        props.refreshPage();
        toast.success(
          `Transaction ${transactionDetails.ticket} ${actionData.actionText} successfully`
        );
      })
      .catch((error) => {
        setOpenTransactionModal(false);
        setApiErrorResponse({
          message: error.response.data.message,
          traceId: error.response.data.traceId,
        });
      });
  };

  return (
    <>
      <Table size="small" align="left" width="100%">
        <THead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TR key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const { key, ...rest } = {
                  key: header.id,
                  colSpan: header.colSpan,
                  style: { width: header.getSize() },
                };
                return (
                  <TH key={key} {...rest}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TH>
                );
              })}
            </TR>
          ))}
        </THead>
        <TBody>
          {table.getRowModel().rows.map((row) => (
            <TR key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TD key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TD>
              ))}
            </TR>
          ))}
        </TBody>
      </Table>

      <TransactionActionModal
        open={openTransactionModal}
        data={transactionDetails}
        onClose={() => setOpenTransactionModal(false)}
        title={getActionData(transactionAction).modalTitle}
        description={transactionDetails.ticket}
        onSubmit={handleTransactionAction}
        action={transactionAction}
      />

      <ResponseErrorModal
        errorDetails={apiErrorResponse}
        isOpen={apiErrorResponse ? true : false}
        onModalClose={() => {
          setApiErrorResponse(null);
        }}
      ></ResponseErrorModal>
    </>
  );
};

export default TransactionsTable;
